import type { ReactNode } from "react";

/*
 * Renders the small Markdown subset our help articles and the assistant's
 * answers use, straight to React elements (no HTML strings, so nothing can
 * inject markup): headings (#, ##, ###), paragraphs, "-" and "1." lists,
 * fenced code blocks, pipe tables, **bold**, `code`, [links](https://…),
 * [links](help:topic) to other articles and, where the caller renders them,
 * [links](/projects/…) to pages of the panel.
 */

export type Block =
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "code"; language: string; text: string }
  | { kind: "table"; header: string[]; rows: string[][] };

const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^[-*]\s+(.*)$/;
const NUMBERED = /^\d+[.)]\s+(.*)$/;
const FENCE = /^(```|~~~)\s*([\w+#.-]*)\s*$/;
const TABLE_ROW = /^\|.*\|$/;
const TABLE_RULE = /^\|(\s*:?-{3,}:?\s*\|)+$/;

const cells = (row: string) =>
  row
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim());

export function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const lines = source.split(/\r?\n/);

  const flush = () => {
    if (paragraph.length) blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
    if (list) blocks.push({ kind: "list", ...list });
    paragraph = [];
    list = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i] ?? "";
    const line = raw.trim();
    const fence = FENCE.exec(line);
    if (fence) {
      flush();
      const code: string[] = [];
      for (i += 1; i < lines.length && lines[i]?.trim() !== fence[1]; i += 1)
        code.push(lines[i] ?? "");
      blocks.push({ kind: "code", language: fence[2] ?? "", text: code.join("\n") });
      continue;
    }
    if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[i + 1]?.trim() ?? "")) {
      flush();
      const header = cells(line);
      const rows: string[][] = [];
      for (i += 2; i < lines.length && TABLE_ROW.test(lines[i]?.trim() ?? ""); i += 1)
        rows.push(cells(lines[i]?.trim() ?? ""));
      i -= 1;
      blocks.push({ kind: "table", header, rows });
      continue;
    }
    if (!line) {
      flush();
      continue;
    }
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    const item = (bullet ?? numbered)?.[1];
    if (heading) {
      flush();
      const [, hashes = "#", text = ""] = heading;
      blocks.push({ kind: "heading", level: hashes.length as 1 | 2 | 3, text });
    } else if (item !== undefined) {
      const ordered = !bullet;
      if (paragraph.length || (list && list.ordered !== ordered)) flush();
      list ??= { ordered, items: [] };
      list.items.push(item);
    } else if (list && raw.startsWith("  ")) {
      // Continuation of the previous list item.
      list.items[list.items.length - 1] += ` ${line}`;
    } else {
      if (list) flush();
      paragraph.push(line);
    }
  }
  flush();
  return blocks;
}

const INLINE = /\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;

export type InlineLink = (topic: string, label: string, key: number) => ReactNode;
/** A link to a page of the panel (a path starting with one `/`). */
export type PanelLink = (path: string, label: string, key: number) => ReactNode;

export function renderInline(
  text: string,
  helpLink: InlineLink,
  panelLink?: PanelLink,
): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE)) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const [, bold, code, label, href] = match;
    if (bold !== undefined) out.push(<strong key={key++}>{bold}</strong>);
    else if (code !== undefined)
      out.push(
        <code key={key++} className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[0.85em]">
          {code}
        </code>,
      );
    else if (href?.startsWith("help:")) out.push(helpLink(href.slice(5), label ?? "", key++));
    else if (href && panelLink && /^\/(?!\/)[\w\-./?=&%$]*$/.test(href))
      out.push(panelLink(href, label ?? "", key++));
    else if (href && /^https:\/\//.test(href))
      out.push(
        <a
          key={key++}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="text-accent underline underline-offset-2"
        >
          {label}
        </a>,
      );
    else out.push(label ?? match[0]);
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** How a fenced code block is drawn; the default is a plain scrolling block. */
export type CodeBlock = (text: string, language: string, key: number) => ReactNode;

const plainCode: CodeBlock = (text, _language, key) => (
  <pre key={key} className="overflow-x-auto rounded-lg bg-surface-2 p-3 font-mono text-xs">
    <code>{text}</code>
  </pre>
);

export function Markdown({
  source,
  helpLink,
  panelLink,
  codeBlock = plainCode,
}: {
  source: string;
  helpLink: InlineLink;
  panelLink?: PanelLink;
  codeBlock?: CodeBlock;
}) {
  const inline = (text: string) => renderInline(text, helpLink, panelLink);
  return (
    <div className="flex min-w-0 flex-col gap-3 text-sm leading-relaxed">
      {parseBlocks(source).map((block, i) => {
        if (block.kind === "heading") {
          const Tag = `h${block.level + 1}` as "h2" | "h3" | "h4";
          return (
            <Tag
              key={i}
              className={block.level === 1 ? "text-lg font-semibold" : "mt-2 font-semibold"}
            >
              {inline(block.text)}
            </Tag>
          );
        }
        if (block.kind === "list") {
          const Tag = block.ordered ? "ol" : "ul";
          return (
            <Tag
              key={i}
              className={`flex flex-col gap-1 pl-5 ${block.ordered ? "list-decimal" : "list-disc"}`}
            >
              {block.items.map((item, j) => (
                <li key={j}>{inline(item)}</li>
              ))}
            </Tag>
          );
        }
        if (block.kind === "code") return codeBlock(block.text, block.language, i);
        if (block.kind === "table")
          return (
            <div key={i} className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr>
                    {block.header.map((cell, j) => (
                      <th key={j} className="border-b border-line px-2 py-1 font-semibold">
                        {inline(cell)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, j) => (
                        <td key={j} className="border-b border-line px-2 py-1 align-top">
                          {inline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        return <p key={i}>{inline(block.text)}</p>;
      })}
    </div>
  );
}
